// src/components/common/DataTable/TablePagination.tsx

import {
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
} from "lucide-react";
import {Select, Button} from "../../ui";
import {clsx} from "clsx";
import type {DataTablePagination} from "./DataTableTypes";
import {Fragment} from "react/jsx-runtime";

interface TablePaginationProps extends DataTablePagination {
  className?: string;
}

const TablePagination: React.FC<TablePaginationProps> = ({
  current = 1,
  pageSize = 25,
  total = 0,
  showSizeChanger = true,
  showQuickJumper = false,
  showTotal = true,
  pageSizeOptions = [10, 25, 50, 100],
  onChange,
  className = "",
}) => {
  const totalPages = Math.ceil(total / pageSize);
  const startItem = total === 0 ? 0 : (current - 1) * pageSize + 1;
  const endItem = Math.min(current * pageSize, total);

  // Generate page numbers to display
  const getPageNumbers = (): (number | string)[] => {
    const pages: (number | string)[] = [];
    const maxVisible = 7; // Maximum number of page buttons to show

    if (totalPages <= maxVisible) {
      // Show all pages if total is less than max visible
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      // Always show first page
      pages.push(1);

      if (current <= 4) {
        // Current page is near the beginning
        for (let i = 2; i <= 5; i++) {
          pages.push(i);
        }
        pages.push("...");
        pages.push(totalPages);
      } else if (current >= totalPages - 3) {
        // Current page is near the end
        pages.push("...");
        for (let i = totalPages - 4; i <= totalPages; i++) {
          pages.push(i);
        }
      } else {
        // Current page is in the middle
        pages.push("...");
        for (let i = current - 1; i <= current + 1; i++) {
          pages.push(i);
        }
        pages.push("...");
        pages.push(totalPages);
      }
    }

    return pages;
  };

  const handlePageChange = (page: number) => {
    if (page >= 1 && page <= totalPages && page !== current) {
      onChange?.(page, pageSize);
    }
  };

  const handlePageSizeChange = (newPageSize: number) => {
    const newCurrent = Math.min(current, Math.ceil(total / newPageSize));
    onChange?.(newCurrent, newPageSize);
  };

  const handleQuickJump = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === "Enter") {
      const target = event.target as HTMLInputElement;
      const page = parseInt(target.value);
      if (!isNaN(page)) {
        handlePageChange(page);
        target.value = "";
      }
    }
  };

  if (total === 0) {
    return null;
  }

  return (
    <div
      className={clsx(
        "flex flex-col sm:flex-row items-center justify-between gap-4 p-4 bg-white dark:bg-dark-800 border-t border-gray-200 dark:border-dark-700",
        className
      )}
    >
      {/* Left side - Total info and page size selector */}
      <div className="flex items-center gap-4">
        {showTotal && (
          <span className="text-sm text-gray-700 dark:text-gray-300">
            Showing <span className="font-medium">{startItem}</span> to{" "}
            <span className="font-medium">{endItem}</span> of{" "}
            <span className="font-medium">{total}</span> results
          </span>
        )}

        {showSizeChanger && (
          <div className="flex items-center gap-2">
            <span className="text-sm text-gray-700 dark:text-gray-300">
              Show
            </span>
            <Select
              value={pageSize.toString()}
              onChange={(e) => handlePageSizeChange(parseInt(e.target.value))}
              options={pageSizeOptions.map((size) => ({
                value: size.toString(),
                label: size.toString(),
              }))}
              className="w-20"
            />
            <span className="text-sm text-gray-700 dark:text-gray-300">
              entries
            </span>
          </div>
        )}
      </div>

      {/* Right side - Pagination controls */}
      <div className="flex items-center gap-2">
        {/* Quick jumper */}
        {showQuickJumper && (
          <div className="flex items-center gap-2 mr-4">
            <span className="text-sm text-gray-700 dark:text-gray-300">
              Go to
            </span>
            <input
              type="number"
              min={1}
              max={totalPages}
              placeholder="Page"
              onKeyDown={handleQuickJump}
              className="w-16 px-2 py-1 text-sm border border-gray-300 dark:border-dark-600 rounded focus:ring-2 focus:ring-primary-500 focus:border-primary-500 dark:bg-dark-700 dark:text-white"
            />
          </div>
        )}

        {/* Pagination buttons */}
        <div className="flex items-center">
          {/* First page */}
          <Button
            variant="ghost"
            size="sm"
            onClick={() => handlePageChange(1)}
            disabled={current === 1}
            className="px-2"
          >
            <ChevronsLeft className="w-4 h-4" />
          </Button>

          {/* Previous page */}
          <Button
            variant="ghost"
            size="sm"
            onClick={() => handlePageChange(current - 1)}
            disabled={current === 1}
            className="px-2"
          >
            <ChevronLeft className="w-4 h-4" />
          </Button>

          {/* Page numbers */}
          <div className="flex items-center mx-2">
            {getPageNumbers().map((page, index) => (
              <Fragment key={index}>
                {page === "..." ? (
                  <span className="px-3 py-1 text-gray-500 dark:text-gray-400">
                    ...
                  </span>
                ) : (
                  <Button
                    variant={current === page ? "primary" : "ghost"}
                    size="sm"
                    onClick={() => handlePageChange(page as number)}
                    className="min-w-[32px] px-2"
                  >
                    {page}
                  </Button>
                )}
              </Fragment>
            ))}
          </div>

          {/* Next page */}
          <Button
            variant="ghost"
            size="sm"
            onClick={() => handlePageChange(current + 1)}
            disabled={current === totalPages}
            className="px-2"
          >
            <ChevronRight className="w-4 h-4" />
          </Button>

          {/* Last page */}
          <Button
            variant="ghost"
            size="sm"
            onClick={() => handlePageChange(totalPages)}
            disabled={current === totalPages}
            className="px-2"
          >
            <ChevronsRight className="w-4 h-4" />
          </Button>
        </div>
      </div>
    </div>
  );
};

export default TablePagination;
